import {
  queryOptions,
  skipToken,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { shotsApi } from '../../data/api/shots.api'
import type { GenerationParams } from '../../data/types/GenerationParams'
import { GENERATION_STATUSES, GenerationStatus } from '../enums/GenerationStatus'
import type { UpsertReferenceAsset } from '../models/ReferenceAsset'
import type { ShotPosition, ShotTree, UpsertShot } from '../models/Shot'

import { storyboardsKeys } from './useStoryboards'
import { queueKeys } from './useQueue'

export const shotsKeys = {
  all: ['shots'] as const,
  list: (commercialId: string | undefined) => ['shots', commercialId] as const,
  reorder: (commercialId: string) => ['shots', commercialId, 'reorder'] as const,
  variant: (id: string | undefined, variantId?: string) =>
    ['shots', variantId ? 'variant' : 'detail', id, variantId] as const,
  status: (id: string, variantId: string) => ['shots', 'status', id, variantId] as const,
}

export function useShots(commercialId: string | undefined) {
  return useQuery({
    queryKey: shotsKeys.list(commercialId),
    queryFn: commercialId ? () => shotsApi.list(commercialId).then((r) => r.data) : skipToken,
  })
}

export function useShotsReorder(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ShotPosition[]) => shotsApi.reorder(commercialId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.list(commercialId) })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShot(
  commercialId: string | undefined,
  shotId: string | undefined,
  variantId?: string,
) {
  return useQuery({
    queryKey: shotsKeys.variant(shotId, variantId),
    queryFn:
      commercialId && shotId
        ? () => shotsApi.detail(commercialId, shotId, variantId).then((r) => r.data)
        : skipToken,
  })
}

export function useShotCreate(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertShot) => shotsApi.create(commercialId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.list(commercialId) })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotCreateVariant(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ shotId, data }: { shotId: string; data: UpsertShot }) =>
      shotsApi.createVariant(commercialId, shotId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.all })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotUpdate(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      shotId,
      variantId,
      data,
    }: {
      shotId: string
      variantId: string
      data: UpsertShot
    }) => shotsApi.update(commercialId, shotId, variantId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.all })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotDelete(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ shotId, variantId }: { shotId: string; variantId?: string }) =>
      shotsApi.delete(commercialId, shotId, variantId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.list(commercialId) })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotSetActive(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ shotId, variantId }: { shotId: string; variantId: string }) =>
      shotsApi.setActive(commercialId, shotId, variantId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.list(commercialId) })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotReferenceCreate(commercialId: string, shotId: string, variantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: UpsertReferenceAsset) =>
      shotsApi.createReference(commercialId, shotId, variantId, params).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.variant(shotId, variantId) })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotReferenceUpdate(commercialId: string, shotId: string, variantId: string) {
  const qc = useQueryClient()
  type MutationParams = { referenceId: string; params: Partial<UpsertReferenceAsset> }
  return useMutation({
    mutationFn: ({ referenceId, params }: MutationParams) =>
      shotsApi
        .updateReference(commercialId, shotId, variantId, referenceId, params)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.variant(shotId, variantId) })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotReferenceDelete(commercialId: string, shotId: string, variantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (referenceId: string) =>
      shotsApi.deleteReference(commercialId, shotId, variantId, referenceId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.variant(shotId, variantId) })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotSetImage(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      shotId,
      variantId,
      imageId,
    }: {
      shotId: string
      variantId: string
      imageId: string
    }) => shotsApi.setImage(commercialId, shotId, variantId, imageId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.all })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotDeleteImages(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (imagePaths: string[]) =>
      shotsApi.deleteImages(commercialId, imagePaths).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shotsKeys.all })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
    },
  })
}

export function useShotGenerateImages(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      shotId,
      variantId,
      params,
    }: {
      shotId: string
      variantId: string
      params: GenerationParams
    }) => shotsApi.generateImages(commercialId, shotId, variantId, params).then((r) => r.data),
    onSuccess: (_, { shotId, variantId }) => {
      qc.invalidateQueries({ queryKey: queueKeys.all })
      qc.invalidateQueries({ queryKey: storyboardsKeys.all })
      qc.setQueryData(shotsKeys.status(shotId, variantId), GenerationStatus.Running)
    },
  })
}

type VariantPair = {
  elementId: string
  variantId: string
}

function generationStatusOptions(
  commercialId: string,
  elementId: string,
  variantId: string,
  enabled = true,
) {
  return queryOptions({
    queryKey: shotsKeys.status(elementId, variantId),
    queryFn: () =>
      shotsApi.generateImagesStatus(commercialId, elementId, variantId).then((r) => r.data.status),
    enabled: !!commercialId && !!elementId && !!variantId && enabled,
    refetchInterval: (query) =>
      GENERATION_STATUSES.some((value) => value === query.state.data) ? 5000 : false,
  })
}

export function useShotGenerateImagesStatus(
  commercialId: string,
  shotId: string,
  variantId: string,
  enabled = false,
) {
  return useQuery(generationStatusOptions(commercialId, shotId, variantId, enabled))
}

type GenerationStatusSets = {
  generatingIds: Set<string>
  errorIds: Set<string>
  clearErrorIds: (variantId?: string) => void
}

export function useShotsGenerationStatuses(
  commercialId: string,
  elements: ShotTree[],
): GenerationStatusSets {
  const qc = useQueryClient()

  const variantsPair = useMemo<VariantPair[]>(() => {
    return elements.flatMap((e) => {
      if (e.versions.length > 0) {
        return e.versions.map((v) => ({ elementId: e.id, variantId: v.id }))
      }
      if (e.currentVersionId) {
        return [{ elementId: e.id, variantId: e.currentVersionId }]
      }
      return []
    })
  }, [elements])

  const results = useQueries({
    queries: variantsPair.map((pair) =>
      generationStatusOptions(commercialId, pair.elementId, pair.variantId),
    ),
  })

  const clearErrorIds = useCallback(
    (variantId?: string) => {
      variantsPair.forEach((pair, index) => {
        if (variantId && pair.variantId !== variantId) return
        const status = results[index].data
        if (status === GenerationStatus.Failed || status === GenerationStatus.Cancelled) {
          qc.setQueryData(shotsKeys.status(pair.elementId, pair.variantId), GenerationStatus.None)
        }
      })
    },
    [variantsPair, results, qc],
  )

  const resultsKey = results.map((r) => r.data).join(',')
  return useMemo<GenerationStatusSets>(() => {
    const generatingIds = new Set<string>()
    const errorIds = new Set<string>()

    results.forEach((result, index) => {
      const status = result.data
      if (!status) return
      const { elementId, variantId } = variantsPair[index]
      if (GENERATION_STATUSES.some((value) => value === status)) {
        generatingIds.add(variantId)
        generatingIds.add(elementId)
      } else if (status === GenerationStatus.Failed || status === GenerationStatus.Cancelled) {
        errorIds.add(variantId)
        errorIds.add(elementId)
      }
    })
    return { generatingIds, errorIds, clearErrorIds }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsKey, variantsPair])
}
